// IR-1771 FE: how an Active Alerts row reads the fields the backend serves per
// open problem (iris-backend api/ops/services/alert_problems.py
// `open_problem_rows`): `fire_count`, `last_seen_at`, and Could Not Check rows
// (`severity`/`state` "could_not_check", `label` "Could Not Check").
//
// Matrix fit: a missing or unusable value shows nothing (the caller renders an
// em dash); nothing is invented, and Could Not Check is never an alert.
import { fmtLocalDateTimeWithZone } from "@/lib/group-created";
import type { AlertInstance } from "@/lib/types";

export const CNC_LABEL = "Could Not Check";

export function isCouldNotCheck(a: Pick<AlertInstance, "severity" | "state">): boolean {
  return a.severity === "could_not_check" || a.state === "could_not_check";
}

/** "Fired 7 times" / "Fired 1 time"; null when the backend sent no usable count. */
export function fireCountText(a: Pick<AlertInstance, "fire_count">): string | null {
  const n = a.fire_count;
  if (typeof n !== "number" || !Number.isFinite(n) || n < 1) return null;
  return `Fired ${n.toLocaleString()} ${n === 1 ? "time" : "times"}`;
}

/** "Last seen 2h ago" plus the exact local time for the hover title; null when absent or unparseable. */
export function lastSeenText(
  a: Pick<AlertInstance, "last_seen_at">,
  nowMs: number = Date.now(),
): { text: string; title: string } | null {
  const iso = a.last_seen_at;
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const mins = Math.max(0, Math.floor((nowMs - t) / 60_000));
  let ago: string;
  if (mins < 1) ago = "just now";
  else if (mins < 60) ago = `${mins}m ago`;
  else if (mins < 24 * 60) ago = `${Math.floor(mins / 60)}h ago`;
  else ago = `${Math.floor(mins / 1440)}d ago`;
  return { text: `Last seen ${ago}`, title: fmtLocalDateTimeWithZone(iso) };
}

/** Alert rows first, Could Not Check rows after, each in the served order. */
export function splitProblems<T extends Pick<AlertInstance, "severity" | "state">>(
  rows: readonly T[],
): { problems: T[]; couldNotCheck: T[] } {
  const problems: T[] = [];
  const couldNotCheck: T[] = [];
  for (const r of rows) (isCouldNotCheck(r) ? couldNotCheck : problems).push(r);
  return { problems, couldNotCheck };
}
